import React, { forwardRef, useImperativeHandle, useState, useRef, useMemo } from "react";
import {
  Modal,
  Form,
  Button,
  Input,
  Card,
  Typography,
  Flex,
  Row,
  Col,
  Spin,
  Empty,
  message,
  Table
} from "antd";
import dayjs from "dayjs";
import http from "@/utils/http";
import api from "@/utils/api";
import timeIcon from "@/assets/images/icons/time.png";
import attachmentIcon from "@/assets/images/icons/attachment.png";
import CustomUploadNewBank from "@/hocComponents/customUploadNewBank";

const detailModal = forwardRef(({ onSuccess }, ref) => {
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [filePaths, setFilePaths] = useState([]);
  const [hasData, setHasData] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [remark, setRemark] = useState("");
  const customUploadNewRef = useRef(null);

  // ===== 转时间字符串 =====
  const formatTimeFromArray = (arr) => {
    if (!arr || !Array.isArray(arr) || arr.length < 5) return "--:-- --";
    
    const hour = arr[3] || 0;
    const minute = arr[4] || 0;
    
    let hour12 = hour % 12;
    if (hour12 === 0) hour12 = 12;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const minuteStr = String(minute).padStart(2, '0');
    
    return `${hour12}:${minuteStr} ${ampm}`;
  };

  // ===== 计算工作时间 =====
  const calculateWorkHours = (startArr, endArr) => {
    if (!startArr || !endArr || !Array.isArray(startArr) || !Array.isArray(endArr) || startArr.length < 5 || endArr.length < 5) {
      return "0 Hour 0 Minutes";
    }
    
    const startTotalMinutes = startArr[3] * 60 + startArr[4];
    const endTotalMinutes = endArr[3] * 60 + endArr[4];
    const diffMinutes = endTotalMinutes - startTotalMinutes;
    
    if (diffMinutes <= 0) return "0 Hour 0 Minutes";
    
    const h = Math.floor(diffMinutes / 60);
    const m = diffMinutes % 60;
    return `${h} Hour ${m} Minutes`;
  };

  // ===== 接口请求函数 =====
  const fetchDailyRecord = async (accountId, recordDate) => {
    if (!accountId || !recordDate) return;
    
    setDetailLoading(true);
    setHasData(false);
    setTableData([]);
    setRemark("");
    
    try {
      const response = await http.get(api.getDailyRecord, {
        params: {
          accountId: accountId,
          recordDate: recordDate,
        },
      });

      if (response?.code === "1") {
        const dayData = response.data;
        
        if (dayData && dayData.workItems?.length) {
          setHasData(true);
          
          const tableDataList = dayData.workItems.map((item, index) => {
            const startTimeStr = formatTimeFromArray(item.startTime);
            const endTimeStr = formatTimeFromArray(item.endTime);
            const workHours = calculateWorkHours(item.startTime, item.endTime);
            
            return {
              key: item.id || index,
              workTime: `${startTimeStr} - ${endTimeStr}`,
              workDescription: item.isLunch ? "Lunch" : item.workDescription || "",
              workHours: workHours,
              isLunch: item.isLunch,
            };
          });
          
          setTableData(tableDataList);
          setRemark(dayData.record?.remark || "");
          
          if (dayData.fileList && dayData.fileList.length > 0) {
            const fileListData = dayData.fileList.map((file) => ({
              id: file.id,
              uuid: file.uuid,
              fileName: file.fileName,
              relativePath: file.relativePath,
              url: file.url,
              allPath: file.url,
              name: file.fileName,
              uid: file.id || `file-${Math.random()}`,
              status: 'done',
            }));
            
            setFilePaths(fileListData);
            
            if (customUploadNewRef.current) {
              customUploadNewRef.current.initUpdateList(fileListData);
            }
          } else {
            setFilePaths([]);
            if (customUploadNewRef.current) {
              customUploadNewRef.current.initUpdateList([]);
            }
          }
        } else {
          setHasData(false);
          setTableData([]);
          setRemark("");
          setFilePaths([]);
          if (customUploadNewRef.current) {
            customUploadNewRef.current.initUpdateList([]);
          }
        }
      } else {
        message.warning(response?.msg);
        setHasData(false);
        setTableData([]);
        setRemark("");
        setFilePaths([]);
        if (customUploadNewRef.current) {
          customUploadNewRef.current.initUpdateList([]);
        }
      }
    } catch (error) {
      console.error('fetchDailyRecord error:', error);
      setHasData(false);
      setTableData([]);
      setRemark("");
      setFilePaths([]);
      if (customUploadNewRef.current) {
        customUploadNewRef.current.initUpdateList([]);
      }
    } finally {
      setDetailLoading(false);
    }
  };

  // ===== useImperativeHandle =====
  useImperativeHandle(ref, () => ({
    open: ({ accountId, date }) => {
      setVisible(true);
      setSelectedAccountId(accountId);
      setSelectedDate(dayjs(date));
      fetchDailyRecord(accountId, date);
    },
    close: () => {
      setVisible(false);
      form.resetFields();
      setSelectedDate(null);
      setSelectedAccountId(null);
      setFilePaths([]);
      setHasData(false);
      setTableData([]);
      setRemark("");
      if (customUploadNewRef.current) {
        customUploadNewRef.current.initUpdateList([]);
      }
    },
  }));

  const addOldFn = async () => {
    return { flag: false, fileData: {} };
  };

  const deleteOldFn = async () => {
    return true;
  };

  const columns = [
    {
      title: 'Work Time',
      dataIndex: 'workTime',
      key: 'workTime',
      width: 340,
    },
    {
      title: 'Work Description',
      dataIndex: 'workDescription',
      key: 'workDescription',
      flex: 1,
    },
    {
      title: 'Work Hours',
      dataIndex: 'workHours',
      key: 'workHours',
      width: 180,
      align: 'center',
    },
  ];

 return (
  <Modal
    open={visible}
    width="80%"
    style={{ top: 20 }}
    maskClosable={false} 
    footer={null}
    onCancel={() => {
      setVisible(false);
      form.resetFields();
      setSelectedDate(null);
      setSelectedAccountId(null);
      setFilePaths([]);
      setHasData(false);
      setTableData([]);
      setRemark("");
      if (customUploadNewRef.current) {
        customUploadNewRef.current.initUpdateList([]);
      }
    }}
  >
    <Spin spinning={detailLoading} tip="Loading...">
      <Typography.Title level={4} style={{ marginBottom: 16 }}>
        {selectedDate?.format("dddd, MMM DD, YYYY")}
      </Typography.Title>
      
      <Row gutter={24}>
        <Col span={14}>
          <Form 
            form={form} 
            layout="vertical"
            style={{ height: '100%' }}
          >
            <Card
              style={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
              title={
                <Flex align="center" gap={8}>
                  <img src={timeIcon} alt="time" width={20} />
                  <span>Time Sheet</span>
                </Flex>
              }
              bodyStyle={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                padding: '24px'
              }}
            >
              {!hasData ? (
                <Empty description="No data for this date" />
              ) : (
                <>
                  <Table
                    dataSource={tableData}
                    columns={columns}
                    pagination={false}
                    size="middle"
                    bordered
                    rowClassName={(record) => record.isLunch ? 'lunch-row' : ''}
                    style={{ flex: 1 }}
                  />
                  
                  {remark && (
                    <div style={{ 
                      marginTop: 'auto',
                      paddingTop: 24,
                      borderTop: '1px solid #f0f0f0'
                    }}>
                      <Typography.Text strong>Remark:</Typography.Text>
                      <div style={{ 
                        marginTop: 8,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        minHeight: '260px'
                      }}>
                        {remark}
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card>
          </Form>
        </Col>

        <Col span={10}>
          <Card
            style={{ 
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
            title={
              <Flex align="center" gap={8}>
                <img src={attachmentIcon} alt="attachment" width={20} />
                <span>Attachment</span>
              </Flex>
            }
            bodyStyle={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <CustomUploadNewBank
                ref={customUploadNewRef}
                customConfig={{
                  pdfHeight: "70vh",
                  notMultiple: true,
                  accept: ".jpg, .jpeg, .png, .pdf",
                  status: 'add',
                  disabled: true,
                  noUploadBtn: true,
                  noDeleteBtn: true,
                  deleteOldFn: deleteOldFn,
                  addOldFn: addOldFn
                }}
                setFilePaths={setFilePaths}
                filePaths={filePaths}
                setBtnDisabled={() => {}}
                btnDisabled={true}
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Flex justify="center" gap={16} style={{ marginTop: 24, paddingTop: 16 }}>
        <Button onClick={() => {
          setVisible(false);
          form.resetFields();
          setSelectedDate(null);
          setSelectedAccountId(null);
          setFilePaths([]);
          setHasData(false);
          setTableData([]);
          setRemark("");
          if (customUploadNewRef.current) {
            customUploadNewRef.current.initUpdateList([]);
          }
        }}>
          Close
        </Button>
      </Flex>
    </Spin>
  </Modal>
);
});

export default detailModal;